import { CommonApiService } from '@services/common-api-service';
import { Component, Prop, State, h } from '@stencil/core';
import { disclaimerStore } from '@store/disclaimer-store';
import { isLocalEnvironment } from '@utils/utilities';
import DOMPurify from 'dompurify';
import { Disclaimer } from '../types';

@Component({
  tag: 'platform-popup',
  styleUrl: 'platform-popup.scss',
  shadow: true
})
export class PlatformPopup {
  @Prop() activeProgram: string;
  @State() shouldRender: boolean = false;
  private apiService = new CommonApiService();

  componentWillLoad() {
    const consentCookie = this.getCookie('disclaimer');
    if (consentCookie === '1') {
      this.shouldRender = false;
    } else {
      setTimeout(() => {
        this.shouldRender = true;
      }, 300);
    }
  }

  private dismissConsent = () => {
    setTimeout(() => {
      this.shouldRender = false;
    }, 300);
  };

  handleProceed = async (value: string) => {
    this.shouldRender = false;

    const formData = new FormData();
    formData.append('name', 'accept-cookie');
    formData.append('value', String(value));
    formData.append('type', 'DISCLAIMER');

    await this.apiService.setCookie('/api/setCookieConsent', formData);
    this.dismissConsent();
  };

  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  get disclaimerContent(): Disclaimer | undefined {
    const data = disclaimerStore.disclaimerData;
    if (data && data['system-default']) {
      return data['system-default'][0]?.disclaimers?.find(({ type }) => type === 'consent_popup');
    } else {
      console.warn('NO DISCLAIMER DATA COMING FROM PLATFORM POPUP');
    }

    return undefined;
  }

  private get sanitizedDisclaimer() {
    const content = this.disclaimerContent;
    return {
      title: content?.title ? DOMPurify.sanitize(content.title) : '',
      body: content?.content ? DOMPurify.sanitize(content.content) : '',
      footer: content?.footerContent
        ? DOMPurify.sanitize(content.footerContent)
        : DOMPurify.sanitize(
          'Thank you for visiting https://offers.smartbuy.hdfcbank.com/. Please click here for detailed terms & conditions.'
        ),
      buttonText: content?.buttonText || 'Continue'
    };
  }

  render() {
    if (!this.shouldRender || !this.disclaimerContent || isLocalEnvironment()) {
      return null;
    }

    const { title, body, footer, buttonText } = this.sanitizedDisclaimer;

    return (
      <div class="popup-wrapper" role="dialog" aria-modal="true" aria-labelledby="popup-title">
        <popup-modal open={true} size="2xl">
          <div class="popup-modal">
            <div class="modal-header">
              <div class="text-left text-xs space-y-4">
                <div class="flex justify-between items-center">
                  {title && (
                    <div class="text-sm font-semibold" innerHTML={title}></div>
                  )}
                  <button class="close-button cursor-pointer" aria-label="Close popup" onClick={() => this.handleProceed('1')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-body">
              {body && (
                <div class="text-xs text-gray-700" innerHTML={body}></div>
              )}
            </div>

            <div class="modal-footer flex justify-between items-center flex-col md:flex-row gap-3 md:gap-2">
              <div class="text-xs text-gray-700" innerHTML={footer}></div>
              <div>
                <custom-button
                  variant="primary"
                  onButtonClick={() => this.handleProceed('1')}
                  class="w-full md:w-auto"
                >
                  {buttonText}
                </custom-button>
              </div>
            </div>
          </div>
        </popup-modal>
      </div>
    );
  }
}
